import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Minus, GitCompareArrows, Sparkles, FileSpreadsheet, FileText, AlertTriangle } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/sortable";

const fmtNum = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES").format(Math.round(n)));
const fmtNum2 = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n));
const fmtEur = (n) =>
  n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

function pctClass(n, invert = false) {
  if (n == null) return "text-slate-400";
  if (n === 0) return "text-slate-500";
  const positive = invert ? n < 0 : n > 0;
  return positive ? "text-emerald-600" : "text-rose-600";
}

function PctBadge({ value, invert = false, suffix = "%" }) {
  const cls = pctClass(value, invert);
  const Icon = value == null || value === 0 ? Minus : (invert ? value < 0 : value > 0) ? TrendingUp : TrendingDown;
  const text = value == null ? "—" : `${value > 0 ? "+" : ""}${value.toFixed(1)}${suffix}`;
  return (
    <span className={`inline-flex items-center gap-1 font-mono-num font-semibold text-xs ${cls}`}>
      <Icon className="h-3 w-3" strokeWidth={2} />
      {text}
    </span>
  );
}

function KpiDeltaCard({ label, a, b, deltaPct, formatter = fmtNum, invert = false, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">{label}</div>
      <div className="flex items-baseline gap-3">
        <span className="font-display text-2xl font-bold text-slate-400 line-through decoration-slate-300">{formatter(a)}</span>
        <span className="text-slate-300">→</span>
        <span className="font-display text-3xl font-bold text-slate-950">{formatter(b)}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <PctBadge value={deltaPct} invert={invert} />
        {hint && <span className="text-[10px] text-slate-400 uppercase tracking-wider">{hint}</span>}
      </div>
    </div>
  );
}

function MiniChart({ points, dataKey, color = "#0f172a", formatter = fmtNum }) {
  if (!points || points.length === 0) return null;
  const W = 100, H = 30, pad = 2;
  const vals = points.map((p) => p[dataKey] || 0);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const range = max - min || 1;
  const step = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const path = points
    .map((p, i) => {
      const x = pad + i * step;
      const y = H - pad - ((p[dataKey] - min) / range) * (H - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-10">
      <path d={path} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => {
        const x = pad + i * step;
        const y = H - pad - ((p[dataKey] - min) / range) * (H - pad * 2);
        return <circle key={i} cx={x} cy={y} r="0.9" fill={color} />;
      })}
    </svg>
  );
}

function TimelineSection({ timeline }) {
  const sort = useTableSort(timeline);
  if (!timeline || timeline.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500 text-sm">
        Aún no hay cargas suficientes para construir una línea de tiempo. Sube al menos una dispensación.
      </div>
    );
  }

  const charts = [
    { key: "euros_pm", label: "€ / mes", color: "#0f172a", fmt: fmtEur },
    { key: "units_pm", label: "Unidades / mes", color: "#0ea5e9", fmt: fmtNum },
    { key: "generic_share_pct", label: "% conversión a genérico (uds)", color: "#10b981", fmt: (v) => `${v?.toFixed?.(1) ?? "—"}%` },
    { key: "generic_share_eur_pct", label: "% conversión a genérico (€)", color: "#0ea5e9", fmt: (v) => `${v?.toFixed?.(1) ?? "—"}%` },
  ];

  return (
    <div className="space-y-6" data-testid="timeline-section">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {charts.map((c) => {
          const last = timeline[timeline.length - 1];
          const first = timeline[0];
          const lastVal = last?.[c.key];
          const firstVal = first?.[c.key];
          return (
            <div key={c.key} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">{c.label}</div>
              <div className="font-display text-2xl font-bold text-slate-950">{c.fmt(lastVal)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">primero {c.fmt(firstVal)}</div>
              <div className="mt-3">
                <MiniChart points={timeline} dataKey={c.key} color={c.color} formatter={c.fmt} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display text-base font-bold">Evolución periodo a periodo</div>
          <div className="text-xs text-slate-500 mt-0.5">Valores normalizados por mes. % vs el periodo inmediatamente anterior.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="timeline-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <SortTh field="label" sort={sort} className="p-3 font-semibold">Periodo</SortTh>
                <SortTh field="months" sort={sort} align="right" className="p-3 font-semibold">Meses</SortTh>
                <SortTh field="euros_pm" sort={sort} align="right" className="p-3 font-semibold">€ / mes</SortTh>
                <SortTh field="delta_euros_pct" sort={sort} align="right" className="p-3 font-semibold">Δ €</SortTh>
                <SortTh field="units_pm" sort={sort} align="right" className="p-3 font-semibold">u / mes</SortTh>
                <SortTh field="delta_units_pct" sort={sort} align="right" className="p-3 font-semibold">Δ u</SortTh>
                <SortTh field="generic_share_pct" sort={sort} align="right" className="p-3 font-semibold">% gen. (uds)</SortTh>
                <SortTh field="delta_share_pp" sort={sort} align="right" className="p-3 font-semibold">Δ p.p.</SortTh>
                <SortTh field="generic_share_eur_pct" sort={sort} align="right" className="p-3 font-semibold">% gen. (€)</SortTh>
                <SortTh field="delta_share_eur_pp" sort={sort} align="right" className="p-3 font-semibold">Δ p.p.</SortTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sort.sorted.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-slate-900">{p.label}</div>
                      {p.is_partial && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                          title={`Periodo de ${p.days_in_period || "?"} días. Comparaciones absolutas pueden distorsionarse.`}
                          data-testid={`timeline-partial-${p.id}`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.5} /> Parcial
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {p.period_start} → {p.period_end}{p.days_in_period ? ` · ${p.days_in_period} d` : ""}
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono-num text-slate-600">{p.months}</td>
                  <td className="p-3 text-right font-mono-num font-semibold">{fmtEur(p.euros_pm)}</td>
                  <td className="p-3 text-right"><PctBadge value={p.delta_euros_pct} /></td>
                  <td className="p-3 text-right font-mono-num">{fmtNum(p.units_pm)}</td>
                  <td className="p-3 text-right"><PctBadge value={p.delta_units_pct} /></td>
                  <td className="p-3 text-right font-mono-num">{p.generic_share_pct?.toFixed(1)}%</td>
                  <td className="p-3 text-right">
                    {p.delta_share_pp == null ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : (
                      <PctBadge value={p.delta_share_pp} suffix=" p.p." />
                    )}
                  </td>
                  <td className="p-3 text-right font-mono-num text-sky-700">{p.generic_share_eur_pct?.toFixed(1)}%</td>
                  <td className="p-3 text-right">
                    {p.delta_share_eur_pp == null ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : (
                      <PctBadge value={p.delta_share_eur_pp} suffix=" p.p." />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PairSection({ timeline }) {
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Default selection: first vs last
  useEffect(() => {
    if (!timeline || timeline.length < 2) return;
    if (!aId) setAId(timeline[timeline.length - 2].id);
    if (!bId) setBId(timeline[timeline.length - 1].id);
  }, [timeline, aId, bId]);

  const swap = () => {
    const x = aId;
    setAId(bId);
    setBId(x);
  };

  const labSort = useTableSort(data?.laboratorios || [], {
    a_eur: (l) => l.a?.euros_pm,
    b_eur: (l) => l.b?.euros_pm,
  });

  useEffect(() => {
    if (!aId || !bId || aId === bId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError("");
    api
      .get(`/comparison/pair`, { params: { a: aId, b: bId } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error al comparar"))
      .finally(() => setLoading(false));
  }, [aId, bId]);

  if (!timeline || timeline.length < 2) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500 text-sm">
        Necesitas al menos <strong>dos</strong> dispensaciones cargadas para comparar.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pair-section">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Periodo A (base)</div>
            <select
              data-testid="pair-select-a"
              value={aId}
              onChange={(e) => setAId(e.target.value)}
              className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-md bg-white"
            >
              {timeline.map((p) => (
                <option key={p.id} value={p.id}>
                  {`${p.label} · ${p.period_start} → ${p.period_end} (${p.months} m)`}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={swap}
            data-testid="pair-swap"
            className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
            title="Intercambiar A ↔ B"
          >
            <GitCompareArrows className="h-4 w-4" strokeWidth={1.5} />
            Cambiar
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Periodo B (comparado)</div>
            <select
              data-testid="pair-select-b"
              value={bId}
              onChange={(e) => setBId(e.target.value)}
              className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-md bg-white"
            >
              {timeline.map((p) => (
                <option key={p.id} value={p.id}>
                  {`${p.label} · ${p.period_start} → ${p.period_end} (${p.months} m)`}
                </option>
              ))}
            </select>
          </div>
        </div>
        {aId === bId && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Selecciona dos periodos diferentes.
          </div>
        )}
        {data && (data.a?.is_partial || data.b?.is_partial) && (
          <div
            className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2"
            data-testid="pair-partial-warning"
          >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-700" />
            <span>
              <strong>Período parcial:</strong>{" "}
              {data.a?.is_partial && (<>Estudio A cubre {data.a?.days_in_period || "?"} días. </>)}
              {data.b?.is_partial && (<>Estudio B cubre {data.b?.days_in_period || "?"} días. </>)}
              Los KPIs por mes están normalizados, pero las diferencias absolutas pueden estar sesgadas.
            </span>
          </div>
        )}
      </div>

      {loading && <div className="text-center text-slate-500 text-sm py-8">Calculando comparativa…</div>}
      {error && <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-4 py-3 text-sm">{error}</div>}

      {data && !loading && (
        <>
          {/* Banner de victorias (cuando hay mejoras de A → B) */}
          {data.wins_summary && (
            <div
              data-testid="wins-summary-banner"
              className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white p-6 shadow-xl border border-emerald-500/30 mb-6"
            >
              <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-amber-300/20 blur-2xl pointer-events-none" />
              <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-sky-400/15 blur-2xl pointer-events-none" />
              <div className="relative flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-amber-400/25 grid place-items-center shrink-0 shadow-inner">
                  <Sparkles className="h-6 w-6 text-amber-200" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-200 mb-1.5">
                    ¡Buenas noticias!
                  </div>
                  <h2 className="font-display text-xl sm:text-2xl font-extrabold leading-tight tracking-tight mb-3">
                    {data.wins_summary.headline}
                  </h2>
                  <ul className="space-y-1.5 text-sm text-white/95">
                    {data.wins_summary.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2" data-testid={`win-bullet-${i}`}>
                        <span className="text-amber-300 mt-0.5 shrink-0">●</span>
                        <span>
                          {String(b).split(/\*\*(.+?)\*\*/g).map((part, j) =>
                            j % 2 === 1
                              ? <strong key={j} className="text-amber-200">{part}</strong>
                              : <span key={j}>{part}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Beneficio total destacado */}
          <div
            data-testid="benefit-card"
            className={`rounded-lg p-6 border-2 ${
              (data.delta.benefit_eur || 0) >= 0
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"
            }`}
          >
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600 mb-2">
                  Beneficio por conversión a genérico (B vs A)
                </div>
                <div
                  className={`font-display text-5xl font-bold tracking-tighter ${
                    (data.delta.benefit_eur || 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                  }`}
                >
                  {(data.delta.benefit_eur || 0) >= 0 ? "+" : ""}
                  {fmtEur(data.delta.benefit_eur)}
                </div>
                <div className="text-xs text-slate-600 mt-2 max-w-xl">
                  Δ unidades de genérico/mes × PVL conjunto del G.H., agregado por moléculas. Refleja el ahorro/beneficio neto al sustituir marca por genérico.
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">% conversión</div>
                <div className="flex flex-col gap-1 items-end">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">uds</span>
                    <div className="font-display text-xl font-bold text-slate-950">
                      {data.a.generic_share_pct?.toFixed(1)}% → {data.b.generic_share_pct?.toFixed(1)}%
                    </div>
                    <PctBadge value={data.delta.generic_share_pp} suffix=" p.p." />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">€</span>
                    <div className="font-display text-xl font-bold text-slate-950">
                      {data.a.generic_share_eur_pct?.toFixed(1)}% → {data.b.generic_share_eur_pct?.toFixed(1)}%
                    </div>
                    <PctBadge value={data.delta.generic_share_eur_pp} suffix=" p.p." />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KPIs delta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiDeltaCard label="Unidades / mes" a={data.a.units_pm} b={data.b.units_pm} deltaPct={data.delta.units_pct} />
            <KpiDeltaCard label="Euros / mes" a={data.a.euros_pm} b={data.b.euros_pm} deltaPct={data.delta.euros_pct} formatter={fmtEur} />
            <KpiDeltaCard
              label="% Gen. unidades"
              a={data.a.generic_share_pct}
              b={data.b.generic_share_pct}
              deltaPct={data.delta.generic_share_pp}
              formatter={(v) => `${Number(v || 0).toFixed(1)}%`}
              hint="Subir es mejor (p.p.)"
            />
            <KpiDeltaCard
              label="% Gen. euros"
              a={data.a.generic_share_eur_pct}
              b={data.b.generic_share_eur_pct}
              deltaPct={data.delta.generic_share_eur_pp}
              formatter={(v) => `${Number(v || 0).toFixed(1)}%`}
              hint="Subir es mejor (p.p.)"
            />
            <KpiDeltaCard
              label="Marca u / mes"
              a={data.a.marca_units_pm}
              b={data.b.marca_units_pm}
              deltaPct={data.delta.marca_units_pct}
              invert
              hint="Bajar es mejor"
            />
            <KpiDeltaCard
              label="Genérico u / mes"
              a={data.a.generico_units_pm}
              b={data.b.generico_units_pm}
              deltaPct={data.delta.generico_units_pct}
              hint="Subir es mejor"
            />
          </div>

          {/* Top 25 mejor / peor */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <MoleculesTopTable
              title="Top 25 — Donde más hemos mejorado (€)"
              tone="positive"
              rows={data.top_25_best}
              testid="top-best"
              dispA={data.a.id}
              dispB={data.b.id}
              kind="best"
            />
            <MoleculesTopTable
              title="Top 25 — Donde menos hemos mejorado o hemos empeorado (€)"
              tone="negative"
              rows={data.top_25_worst}
              testid="top-worst"
              dispA={data.a.id}
              dispB={data.b.id}
              kind="worst"
            />
          </div>

          {/* Laboratorios */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="font-display text-base font-bold">Cambio por laboratorio</div>
              <div className="text-xs text-slate-500 mt-0.5">Cuota en € (genéricos dispensados) y variación A → B normalizada por mes.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="labs-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <SortTh field="laboratorio" sort={labSort} className="p-3 font-semibold">Laboratorio</SortTh>
                    <SortTh field="a_eur" sort={labSort} align="right" className="p-3 font-semibold">A €/mes</SortTh>
                    <SortTh field="b_eur" sort={labSort} align="right" className="p-3 font-semibold">B €/mes</SortTh>
                    <SortTh field="delta_euros_pct" sort={labSort} align="right" className="p-3 font-semibold">Δ €</SortTh>
                    <SortTh field="a_share_pct" sort={labSort} align="right" className="p-3 font-semibold">A cuota</SortTh>
                    <SortTh field="b_share_pct" sort={labSort} align="right" className="p-3 font-semibold">B cuota</SortTh>
                    <SortTh field="delta_share_pp" sort={labSort} align="right" className="p-3 font-semibold">Δ cuota</SortTh>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {labSort.sorted.map((l) => (
                    <tr key={l.laboratorio} className="hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-900">{l.laboratorio}</td>
                      <td className="p-3 text-right font-mono-num text-slate-500">{fmtEur(l.a.euros_pm)}</td>
                      <td className="p-3 text-right font-mono-num font-semibold">{fmtEur(l.b.euros_pm)}</td>
                      <td className="p-3 text-right"><PctBadge value={l.delta_euros_pct} /></td>
                      <td className="p-3 text-right font-mono-num text-slate-500">{l.a_share_pct?.toFixed(1)}%</td>
                      <td className="p-3 text-right font-mono-num font-semibold">{l.b_share_pct?.toFixed(1)}%</td>
                      <td className="p-3 text-right"><PctBadge value={l.delta_share_pp} suffix=" p.p." /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MoleculesTopTable({ title, tone, rows, testid, dispA, dispB, kind }) {
  const accent = tone === "positive" ? "text-emerald-700" : "text-rose-700";
  const sort = useTableSort(rows || [], {
    total_uds: (m) => m.b?.units_pm,
    gen_pm: (m) => m.b?.gen_u_pm,
    conv_pct: (m) => m.b?.gen_share_pct,
  });
  const download = async (format) => {
    if (!dispA || !dispB) return;
    try {
      const r = await api.get("/comparison/pair/top25/export", {
        params: { a: dispA, b: dispB, kind, format },
        responseType: "blob",
      });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `top25_${kind}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // sonner toast might not be in scope here; use alert as fallback
      alert("Error en descarga: " + (e?.message || e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`font-display text-base font-bold ${accent}`}>{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">Beneficio = Δ unidades genérico/mes × PVL conjunto del G.H.</div>
        </div>
        {kind && (
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => download("xlsx")}
              data-testid={`${testid}-xlsx`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-emerald-700 text-white hover:bg-emerald-800"
              title="Descargar en Excel"
            >
              <FileSpreadsheet className="h-3 w-3" /> Excel
            </button>
            <button
              onClick={() => download("pdf")}
              data-testid={`${testid}-pdf`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-700"
              title="Descargar en PDF imprimible"
            >
              <FileText className="h-3 w-3" /> PDF
            </button>
          </div>
        )}
      </div>
      <div className="max-h-[460px] overflow-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="text-left p-3 font-semibold">#</th>
              <SortTh field="gh" sort={sort} className="p-3 font-semibold">Molécula (G.H.)</SortTh>
              <SortTh field="pvl" sort={sort} align="right" className="p-3 font-semibold">PVL</SortTh>
              <SortTh field="total_uds" sort={sort} align="right" className="p-3 font-semibold" title="Total unidades del G.H. (marca+genérico) — ordena por el valor de B">Total uds A→B</SortTh>
              <SortTh field="gen_pm" sort={sort} align="right" className="p-3 font-semibold" title="Ordena por las unidades de genérico de B">Gen u/mes A→B</SortTh>
              <SortTh field="conv_pct" sort={sort} align="right" className="p-3 font-semibold" title="Ordena por el % de conversión de B">% conv. A→B</SortTh>
              <SortTh field="benefit_eur" sort={sort} align="right" className="p-3 font-semibold">Beneficio €</SortTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400 text-xs">Sin datos</td>
              </tr>
            )}
            {sort.sorted.map((m, i) => {
              const aTot = m.a?.units_pm || 0;
              const bTot = m.b?.units_pm || 0;
              const lost = bTot < aTot - 0.5;
              const grew = bTot > aTot + 0.5;
              return (
                <tr key={m.gh} className="hover:bg-slate-50">
                  <td className="p-3 text-slate-400 font-mono-num">{i + 1}</td>
                  <td className="p-3 font-medium text-slate-900 max-w-[260px] truncate" title={m.gh}>{m.gh}</td>
                  <td className="p-3 text-right font-mono-num text-slate-500">{m.pvl == null ? "—" : fmtNum2(m.pvl)}</td>
                  <td className="p-3 text-right font-mono-num text-xs" title={lost ? "Has perdido clientes en este G.H." : grew ? "Has ganado clientes en este G.H." : "Sin cambio relevante"}>
                    {fmtNum2(aTot)} → <strong className={lost ? "text-rose-700" : grew ? "text-emerald-700" : "text-slate-900"}>{fmtNum2(bTot)}</strong>
                    {lost && <span className="ml-1 text-rose-600" aria-hidden>▼</span>}
                    {grew && <span className="ml-1 text-emerald-600" aria-hidden>▲</span>}
                  </td>
                  <td className="p-3 text-right font-mono-num text-xs text-slate-600">
                    {fmtNum2(m.a.gen_u_pm)} → <strong className="text-slate-900">{fmtNum2(m.b.gen_u_pm)}</strong>
                  </td>
                  <td className="p-3 text-right font-mono-num text-xs">
                    {m.a.gen_share_pct?.toFixed(0)}% → <strong>{m.b.gen_share_pct?.toFixed(0)}%</strong>
                  </td>
                  <td className={`p-3 text-right font-mono-num font-bold ${m.benefit_eur >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {m.benefit_eur >= 0 ? "+" : ""}{fmtEur(m.benefit_eur)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ComparisonPage() {
  const [timeline, setTimeline] = useState([]);
  const [anyPartial, setAnyPartial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("timeline");

  useEffect(() => {
    setLoading(true);
    api
      .get("/comparison/timeline")
      .then((r) => {
        setTimeline(r.data?.timeline || []);
        setAnyPartial(!!r.data?.any_partial);
      })
      .finally(() => setLoading(false));
  }, []);

  const tabs = useMemo(
    () => [
      { id: "timeline", label: "Línea de tiempo", testid: "tab-timeline" },
      { id: "pair", label: "Comparar A vs B", testid: "tab-pair" },
      { id: "employee", label: "Persona A vs B", testid: "tab-employee" },
    ],
    []
  );

  return (
    <div className="p-8 lg:p-12 max-w-[1500px]" data-testid="comparison-page">
      {/* Hero banner */}
      <div className="relative mb-8 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="comparison-hero-image">
        <img
          src="/img/farmaclass-comparison.jpg"
          alt="FarmaClass · Lo hacías todo bien. Pero no lo medías todo."
          className="w-full h-[260px] md:h-[300px] object-cover object-top block"
          loading="lazy"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-950/40" />
      </div>

      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Evolución</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Comparativa mes a mes</h1>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          Todos los KPIs se normalizan dividiendo por el número de meses de cada dispensación. Las variaciones se expresan en %, los cambios de cuota en p.p. (puntos porcentuales).
        </p>
        {anyPartial && (
          <div
            className="mt-4 inline-flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900 max-w-2xl"
            data-testid="comparison-partial-badge"
          >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-700" />
            <span>
              <strong>Período parcial detectado.</strong> Al menos una dispensación cubre menos de un mes completo. Los KPIs por mes ya están normalizados, pero las comparaciones absolutas pueden distorsionarse. Revisa los estudios marcados con
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 font-bold uppercase tracking-wider ml-1 text-[9px]">parcial</span>.
            </span>
          </div>
        )}
      </div>

      <div className="border-b border-slate-200 mb-6 flex gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-slate-950 text-slate-950"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-slate-500 text-sm py-12">Cargando…</div>
      ) : tab === "timeline" ? (
        <TimelineSection timeline={timeline} />
      ) : tab === "pair" ? (
        <PairSection timeline={timeline} />
      ) : (
        <EmployeePairSection timeline={timeline} />
      )}
    </div>
  );
}

/* -------- Persona A vs B: mismo empleado en dos estudios -------- */
function EmployeePairSection({ timeline }) {
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/employees")
      .then((r) => {
        // Incluimos también los "pending" (auto-detectados en los estudios).
        // Antes se filtraban, lo que provocaba "Sin empleados" cuando el usuario
        // borraba los manuales pero seguía habiendo desglose de personal en los
        // estudios históricos.
        const emps = r.data?.employees || [];
        setEmployees(emps);
        if (emps.length && !empId) setEmpId(emps[0].id);
      })
      .catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    if (timeline.length >= 2) {
      if (!aId) setAId(timeline[timeline.length - 2].id);
      if (!bId) setBId(timeline[timeline.length - 1].id);
    }
  }, [timeline]);

  useEffect(() => {
    if (!empId || !aId || !bId || aId === bId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    api
      .get(`/employees/${empId}/compare`, { params: { a: aId, b: bId } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se pudo comparar"))
      .finally(() => setLoading(false));
  }, [empId, aId, bId]);

  if (timeline.length < 2) {
    return (
      <div className="text-center text-slate-500 text-sm py-12 bg-slate-50 rounded-lg border border-slate-200" data-testid="employee-pair-empty">
        Necesitas al menos <strong>dos</strong> estudios con desglose de personal cargados para comparar.
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="text-center text-slate-500 text-sm py-12 bg-slate-50 rounded-lg border border-slate-200" data-testid="employee-pair-no-employees">
        No se encontraron empleados en el equipo. Ve a <strong>Empleados</strong> para dar de alta a tu personal
        o sube un estudio con desglose por trabajador para que se detecten automáticamente.
      </div>
    );
  }

  const swap = () => { const tmp = aId; setAId(bId); setBId(tmp); };

  return (
    <div className="space-y-6" data-testid="employee-pair-section">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end bg-white rounded-xl border border-slate-200 p-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Empleado</label>
          <select
            value={empId}
            onChange={(e) => setEmpId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="employee-pair-select-emp"
          >
            {employees.length === 0 && <option value="">Sin empleados</option>}
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}{e.role ? ` · ${e.role}` : ""}{e.pending ? " (pendiente)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Estudio A</label>
          <div className="flex gap-2">
            <select
              value={aId}
              onChange={(e) => setAId(e.target.value)}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="employee-pair-select-a"
            >
              {timeline.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <button
              onClick={swap}
              className="px-2 py-2 border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50"
              title="Intercambiar A y B"
              data-testid="employee-pair-swap"
            >
              <GitCompareArrows className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Estudio B</label>
          <select
            value={bId}
            onChange={(e) => setBId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="employee-pair-select-b"
          >
            {timeline.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {aId === bId && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          Elige dos estudios distintos para comparar.
        </div>
      )}
      {loading && <div className="text-center text-slate-500 py-8">Cargando comparación…</div>}
      {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="employee-pair-error">{error}</div>}

      {data && (
        <>
        <div className="grid md:grid-cols-3 gap-4" data-testid="employee-pair-result">
          <EmpCol title="Estudio A" side={data.a} accent="bg-slate-100 text-slate-700" />
          <EmpCol title="Estudio B" side={data.b} accent="bg-slate-950 text-white" />
          <div className="rounded-xl border border-slate-200 p-5 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Evolución (B − A)</div>
            <DeltaRow label="Uds. total" value={data.delta.units_total} format="num" />
            <DeltaRow label="Uds. marca" value={data.delta.units_marca} format="num" invert />
            <DeltaRow label="Uds. genérico" value={data.delta.units_generico} format="num" />
            <DeltaRow label="€ total" value={data.delta.eur_total} format="eur" />
            <DeltaRow label="€ marca perdida" value={data.delta.eur_marca_perdida} format="eur" invert />
            <div className="mt-3 pt-3 border-t border-slate-200">
              <DeltaRow label="% Cambio (uds)" value={data.delta.generic_share_units_pp} format="pp" />
              <DeltaRow label="% Cambio (€)" value={data.delta.generic_share_eur_pp} format="pp" />
            </div>
            <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
              <strong>pp</strong> = puntos porcentuales. Verde = mejora, rojo = empeoramiento. Para {"Uds. marca"} y {"€ marca perdida"} un delta negativo es bueno.
            </div>
          </div>
        </div>
        <EmployeeMoleculesDelta empId={empId} aId={aId} bId={bId} />
        </>
      )}
    </div>
  );
}

function EmployeeMoleculesDelta({ empId, aId, bId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [minOpp, setMinOpp] = useState(3);

  useEffect(() => {
    if (!empId || !aId || !bId || aId === bId) return;
    setData(null);
    setError(null);
    api
      .get(`/employees/${empId}/compare/molecules`, { params: { a: aId, b: bId, min_opportunities: minOpp } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se pudieron calcular las moléculas"));
  }, [empId, aId, bId, minOpp]);

  return (
    <div className="space-y-4" data-testid="employee-molecules-delta">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">Moléculas: dónde ha mejorado y dónde ha empeorado</h3>
          <p className="text-slate-500 text-sm">
            Comparación del % de cambio a genérico de este empleado molécula a molécula entre los dos estudios.
            {data ? ` ${data.molecules_compared} moléculas con volumen en ambos periodos.` : ""}
          </p>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Vol. mínimo por periodo
          <input
            type="number"
            min={1}
            value={minOpp}
            onChange={(e) => setMinOpp(Math.max(1, Number(e.target.value) || 1))}
            className="w-20 px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono-num"
            data-testid="employee-molecules-min-opp"
          />
        </label>
      </div>

      {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-3">{error}</div>}
      {!error && !data && <div className="text-center text-slate-500 py-6 text-sm">Calculando moléculas…</div>}

      {data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <EmployeeMoleculesTable
            title="Top 25 · Donde más ha mejorado"
            rows={data.best}
            aLabel={data.a_label}
            bLabel={data.b_label}
            positive
            testid="employee-molecules-best"
          />
          <EmployeeMoleculesTable
            title="Top 25 · Donde ha empeorado"
            rows={data.worst}
            aLabel={data.a_label}
            bLabel={data.b_label}
            testid="employee-molecules-worst"
          />
        </div>
      )}
    </div>
  );
}

function EmployeeMoleculesTable({ title, rows, aLabel, bLabel, positive = false, testid }) {
  const sort = useTableSort(rows || [], { uds: (r) => r.b_opportunities });
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className={`px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] ${positive ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
        {title}
      </div>
      {!rows?.length ? (
        <div className="p-5 text-sm text-slate-500">
          Sin moléculas {positive ? "con mejora" : "con empeoramiento"} en este par de estudios con el volumen mínimo indicado.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                <SortTh field="gh" sort={sort} className="py-2.5 px-4">Molécula (G.H.)</SortTh>
                <SortTh field="a_pct" sort={sort} align="right" className="py-2.5" title={aLabel}>A</SortTh>
                <SortTh field="b_pct" sort={sort} align="right" className="py-2.5" title={bLabel}>B</SortTh>
                <SortTh field="delta_pp" sort={sort} align="right" className="py-2.5 px-4">Δ</SortTh>
                <SortTh field="uds" sort={sort} align="right" className="py-2.5 px-4">Uds A/B</SortTh>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((r) => (
                <tr key={r.gh} className="border-b border-slate-100">
                  <td className="py-2 px-4 max-w-[260px]">
                    <span className="block truncate text-slate-800" title={r.gh}>{r.gh}</span>
                  </td>
                  <td className="py-2 text-right font-mono-num text-slate-500">{r.a_pct.toFixed(1)}%</td>
                  <td className="py-2 text-right font-mono-num font-semibold">{r.b_pct.toFixed(1)}%</td>
                  <td className={`py-2 px-4 text-right font-mono-num font-bold ${r.delta_pp >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.delta_pp > 0 ? "+" : ""}{r.delta_pp.toFixed(1)} pp
                  </td>
                  <td className="py-2 px-4 text-right font-mono-num text-xs text-slate-500">
                    {r.a_opportunities}/{r.b_opportunities}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmpCol({ title, side, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className={`px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] ${accent}`}>
        {title}
      </div>
      <div className="p-5 space-y-2">
        <div className="text-sm font-semibold text-slate-900 truncate" title={side.label}>{side.label}</div>
        <div className="text-xs text-slate-500 font-mono-num">{side.period_start} → {side.period_end}</div>
        <div className="mt-4 space-y-1.5 text-sm">
          <StatLine k="Uds. total" v={fmtNum(side.units_total)} />
          <StatLine k="Uds. marca" v={fmtNum(side.units_marca)} />
          <StatLine k="Uds. genérico" v={fmtNum(side.units_generico)} />
          <StatLine k="€ total" v={fmtEur(side.eur_total)} />
          <StatLine k="€ marca perdida" v={fmtEur(side.eur_marca_perdida)} muted />
          <div className="pt-2 mt-2 border-t border-slate-100">
            <StatLine k="% Cambio (uds)" v={`${side.generic_share_units.toFixed(1)}%`} bold />
            <StatLine k="% Cambio (€)" v={`${side.generic_share_eur.toFixed(1)}%`} bold />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatLine({ k, v, bold = false, muted = false }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-xs ${muted ? "text-slate-400" : "text-slate-500"}`}>{k}</span>
      <span className={`font-mono-num ${bold ? "font-bold text-slate-900" : "text-slate-800"}`}>{v}</span>
    </div>
  );
}

function DeltaRow({ label, value, format, invert = false }) {
  const positive = invert ? value < 0 : value > 0;
  const cls = value === 0 || value == null ? "text-slate-500" : positive ? "text-emerald-600" : "text-rose-600";
  const Icon = value === 0 || value == null ? Minus : positive ? TrendingUp : TrendingDown;
  let txt = "—";
  if (value != null) {
    const sign = value > 0 ? "+" : "";
    if (format === "num") txt = `${sign}${fmtNum(value)}`;
    else if (format === "eur") txt = `${sign}${fmtEur(value)}`;
    else if (format === "pp") txt = `${sign}${value.toFixed(1)} pp`;
  }
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`inline-flex items-center gap-1 font-mono-num text-sm font-semibold ${cls}`}>
        <Icon className="h-3.5 w-3.5" />
        {txt}
      </span>
    </div>
  );
}
